import React, { useRef, useState } from "react"
import { mutationRequest } from "@util/request"
import { Button } from "@components/shared/Button"
import { DirectUpload } from "@rails/activestorage"
import Cropper from "react-cropper"
import "cropperjs/dist/cropper.css"
import { PersonFill } from "react-bootstrap-icons"
import { toast } from "react-toastify"
import { convertHeicToJpeg } from "@util/heicConversion"

interface User {
  id: number
  avatar_url_medium?: string
}

interface UploadAvatarSectionProps {
  user: User
}

interface CropperInstance {
  getCroppedCanvas: () => HTMLCanvasElement
}

export const UploadAvatarSection: React.FC<UploadAvatarSectionProps> = ({
  user,
}) => {
  const [avatarURL, setAvatarURL] = useState(user.avatar_url_medium || "")
  const [btnActive, setBtnActive] = useState(true)
  const [cropper, setCropper] = useState<CropperInstance | null>(null)
  const [uploadedFilePath, setUploadedFilePath] = useState("")

  const hiddenFileInput = useRef<HTMLInputElement>(null)

  const handleClick = (event: React.MouseEvent) => {
    hiddenFileInput.current?.click()
  }

  const reader = new FileReader()
  reader.onload = (e: ProgressEvent<FileReader>) => {
    setUploadedFilePath(e.target?.result as string)
  }

  const onUploadAvatar = async (the_attachment: File) => {
    setBtnActive(false)
    try {
      const file = await convertHeicToJpeg(the_attachment)
      reader.readAsDataURL(file)
    } catch (e: any) {
      toast.error(e.message || "Sorry! That photo couldn't be processed.")
    } finally {
      setBtnActive(true)
    }
  }

  const onRemovePhoto = (e: React.MouseEvent) => {
    e.preventDefault()
    setBtnActive(false)
    mutationRequest({
      path: `/api/v1/users/${user.id}/remove_avatar`,
      options: { method: "PUT" },
    }).then((response) => {
      setAvatarURL("")
      setBtnActive(true)
    })
  }

  const onCancel = () => {
    setBtnActive(false)
    if (hiddenFileInput.current) {
      hiddenFileInput.current.value = ""
    }
    setUploadedFilePath("")
    setBtnActive(true)
  }

  const onSave = () => {
    if (!cropper) return

    setBtnActive(false)

    cropper.getCroppedCanvas({ maxWidth: 400, maxHeight: 400 }).toBlob((blob) => {
      if (!blob) {
        setBtnActive(true)
        return
      }

      const upload = new DirectUpload(
        new File([blob], "cropped-photo"),
        "/rails/active_storage/direct_uploads",
      )

      upload.create((error, blob) => {
        if (error) {
          console.error("error:", error)
          setBtnActive(true)
        } else {
          const signed_id = blob.signed_id
          const request_body = {
            avatar: signed_id,
          }
          handleUpdateUserData(user.id, request_body).then((response) => {
            if (response && response.user && response.user.avatar_url_medium)
              setAvatarURL(response.user.avatar_url_medium)
            if (hiddenFileInput.current) {
              hiddenFileInput.current.value = ""
            }
            setUploadedFilePath("")
            setBtnActive(true)
          })
        }
      })
    })
  }

  return (
    <div className="grid grid-cols-12 mt-10">
      <label className="block col-span-12 text-gray-900 text-xs uppercase font-bold">
        Profile Image
      </label>
      <div className="col-span-12 lg:col-span-2">
        {avatarURL && avatarURL.length ? (
          <img className="rounded-sm w-full" src={avatarURL} alt="Profile" />
        ) : (
          <PersonFill className="w-full col-span-12 text-gray-500 h-64 block bg-gray-200" />
        )}
        <Button
          disabled={!btnActive}
          bg="bg-green-700"
          className="mt-2 w-full"
          onClick={handleClick}
        >
          Upload an Image
        </Button>
        <input
          type="file"
          ref={hiddenFileInput}
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) onUploadAvatar(file)
          }}
        />
        <div className="mt-2 text-center">
          <button
            className="hover:text-blue-700"
            disabled={!btnActive}
            style={{ cursor: "pointer" }}
            onClick={onRemovePhoto}
          >
            Remove Photo
          </button>
        </div>
      </div>
      {uploadedFilePath && uploadedFilePath.length ? (
        <div className="mx-3 col-span-12 lg:col-span-3 w-full bg-gray-200">
          <h6 className="px-3 font-bold">Crop your photo</h6>
          <Cropper
            className="mt-2"
            initialAspectRatio={1}
            aspectRatio={1}
            src={uploadedFilePath}
            viewMode={1}
            guides={true}
            minCropBoxHeight={10}
            minCropBoxWidth={10}
            onInitialized={(instance) => {
              setCropper(instance)
            }}
          />
          <div className="px-3 py-2">
            <Button
              disabled={!btnActive}
              bg="bg-green-700"
              className="mr-2"
              onClick={onSave}
            >
              Save
            </Button>
            <Button disabled={!btnActive} bg="bg-gray-400" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

const handleUpdateUserData = async (
  userId: number,
  userUpdateData: Record<string, any>,
) => {
  return mutationRequest({
    path: `/api/v1/users/${userId}`,
    options: {
      method: "PUT",
      body: JSON.stringify({
        user: userUpdateData,
      }),
    },
  })
}
